import { Component, Input, OnInit } from '@angular/core';
import { Route, Router, ActivatedRoute } from '@angular/router';

import * as util from '../../utils/util';

import { ThfBreadcrumb, ThfPageAction, ThfDialogService, ThfDialogConfirmOptions, ThfNotificationService } from '@totvs/thf-ui';

import { ThfPageDynamicDetailActions } from './thf-page-dynamic-detail-actions.interface';
import { ThfPageDynamicDetailField } from './thf-page-dynamic-detail-field.interface';
import { ThfPageDynamicService } from './thf-page-dynamic.service';

export const thfPageDynamicDetailLiteralsDefault = {
  en: {
    pageActionEdit: 'Edit',
    pageActionRemove: 'Delete',
    pageActionBack: 'Back',
    confirmRemoveTitle: 'Confirm delete',
    confirmRemoveMessage: 'Are you sure you want to delete this record? You can not undo this action.',
    removeNotificationSuccess: 'Item deleted successfully.',
    registerNotFound: 'Register not found.'
  },
  es: {
    pageActionEdit: 'Editar',
    pageActionRemove: 'Borrar',
    pageActionBack: 'Regreso',
    confirmRemoveTitle: 'Confirmar la exclusiÃ³n',
    confirmRemoveMessage: 'Â¿EstÃ¡ seguro de que desea eliminar este registro? No puede deshacer esta acciÃ³n.',
    removeNotificationSuccess: 'Elemento eliminado con Ã©xito.',
    registerNotFound: 'Registro no encontrado.'
  },
  pt: {
    pageActionEdit: 'Editar',
    pageActionRemove: 'Excluir',
    pageActionBack: 'Voltar',
    confirmRemoveTitle: 'Confirmar exclusÃ£o',
    confirmRemoveMessage: 'Tem certeza de que deseja excluir esse registro? VocÃª nÃ£o poderÃ¡ desfazer essa aÃ§Ã£o.',
    removeNotificationSuccess: 'Item excluÃ­do com sucesso.',
    registerNotFound: 'Registro nÃ£o encontrado.'
  }
};

/**
 * @description
 *
 * O `thf-page-dynamic-detail` Ã© uma pÃ¡gina que serve para exibir registros em detalhes,
 * o mesmo tambÃ©m suporta metadados conforme especificado na documentaÃ§Ã£o.
 *
 * @example
 *
 * <example name="thf-page-dynamic-detail-basic" title="Totvs Page Dynamic Detail Basic">
 *  <file name="sample-thf-page-dynamic-detail-basic/sample-thf-page-dynamic-detail-basic.component.html"> </file>
 *  <file name="sample-thf-page-dynamic-detail-basic/sample-thf-page-dynamic-detail-basic.component.ts"> </file>
 * </example>
 */
@Component({
  selector: 'thf-page-dynamic-detail',
  templateUrl: './thf-page-dynamic-detail.component.html',
  providers: [ ThfPageDynamicService ]
})
export class ThfPageDynamicDetailComponent implements OnInit {

  private _actions: ThfPageDynamicDetailActions = {};
  private _autoRouter: boolean = false;
  private _duplicates: Array<any> = [];
  private _fields: Array<any> = [];
  private _keys: Array<any> = [];
  private _pageActions: Array<ThfPageAction> = [];

  literals = {
    ...thfPageDynamicDetailLiteralsDefault[util.thfLocaleDefault],
    ...thfPageDynamicDetailLiteralsDefault[util.browserLanguage()]
  };
  model: any = {};

  /**
   * @optional
   *
   * @description
   *
   * Define as aÃ§Ãµes da pÃ¡gina de acordo com a interface `ThfPageDynamicDetailActions`.
   */
  @Input('t-actions') set actions(value: ThfPageDynamicDetailActions) {
    this._actions = this.isObject(value) ? value : {};

    this._pageActions = this.getPageActions(this._actions);
  }

  /**
   * @todo Validar rotas na mÃ£o pois se existir uma rota '**' o catch do navigation nÃ£o funciona.
   *
   * @optional
   *
   * @description
   *
   * Cria automaticamente as rotas de ediÃ§Ã£o (novo/duplicate) e detalhes caso as aÃ§Ãµes
   * estejam definidas nas aÃ§Ãµes.
   *
   * > Para o correto funcionamento nÃ£o pode haver nenhum rota coringa (`**`) especificada.
   *
   * @default false
   */
  @Input('t-auto-router') set autoRouter(value: boolean) {
    this._autoRouter = util.convertToBoolean(value);
  }

  get autoRouter(): boolean {
    return this._autoRouter;
  }

  /** Objeto com propriedades do breadcrumb. */
  @Input('t-breadcrumb') breadcrumb?: ThfBreadcrumb = { items: [] };

  /** Lista dos campos exibidos na pÃ¡gina. */
  @Input('t-fields') set fields(value: Array<ThfPageDynamicDetailField>) {
    this._fields = Array.isArray(value) ? [...value] : [];

    this._keys = this.getKeysByFields(this.fields);
    this._duplicates = this.getDuplicatesByFields(this.fields);
  }

  get fields(): Array<ThfPageDynamicDetailField> {
    return this._fields;
  }

  /** TÃ­tulo da pÃ¡gina. */
  @Input('t-title') title: string;

  /**
   * @description
   *
   * Endpoint usado pelo template para requisiÃ§Ã£o do recurso que serÃ£o exibido.
   *
   * Caso a aÃ§Ã£o `remove` estiver configurada, serÃ¡ feito uma requisiÃ§Ã£o de exclusÃ£o nesse mesmo endpoint passando os campos
   * setados como `key: true`.
   *
   * > `DELETE {end-point}/{keys}`
   *
   * ```
   *  <thf-page-dynamic-detail
   *    [t-actions]="{ remove: '/' }"
   *    [t-fields]="[ { property: 'id', key: true } ]"
   *    t-service="/api/thf-samples/v1/people"
   *    ...>
   *  </thf-page-dynamic-detail>
   * ```
   *
   * ResquisiÃ§Ã£o disparada, onde a propriedade `id` Ã© igual a 2:
   *
   * ```
   *  DELETE /api/thf-samples/v1/people/2 HTTP/1.1
   *  Host: localhost:4000
   *  Connection: keep-alive
   *  Accept: application/json, text/plain
   *  ...
   * ```
   *
   * > Caso esteja usando metadados com o template, serÃ¡ disparado uma requisiÃ§Ã£o na inicializaÃ§Ã£o do template para buscar
   * > os metadados da pÃ¡gina passando o tipo do metadado esperado e a versÃ£o cacheada pelo browser.
   * >
   * > `GET {end-point}/metadata?type=detail&version={version}`
   */
  @Input('t-service-api') serviceApi: string;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private thfNotification: ThfNotificationService,
    private thfDialogService: ThfDialogService,
    private thfPageDynamicService: ThfPageDynamicService) {
  }

  ngOnInit(): void {
    const paramId = this.activatedRoute.snapshot.params['id'];

    if (this.activatedRoute.snapshot.data.serviceApi) {
      this.serviceApi = this.activatedRoute.snapshot.data.serviceApi;

      this.thfPageDynamicService.configServiceApi({ endpoint: this.serviceApi });

      this.loadMetadata(paramId);
    } else {
      this.thfPageDynamicService.configServiceApi({ endpoint: this.serviceApi });

      this.loadData(paramId);
    }
  }

  get duplicates() {
    return [...this._duplicates];
  }

  get keys() {
    return [...this._keys];
  }

  get pageActions() {
    return [...this._pageActions];
  }

  private confirmRemove(path) {
    const confirmOptions: ThfDialogConfirmOptions = {
      title: this.literals.confirmRemoveTitle,
      message: this.literals.confirmRemoveMessage,
      confirm: this.remove.bind(this, path)
    };

    this.thfDialogService.confirm(confirmOptions);
  }

  private formatUniqueKey(item) {
    const keys = util.mapObjectByProperties(item, this.keys);

    return util.valuesFromObject(keys).join('|');
  }

  private goBack(/*path*/) {
    window.history.back();
    // if (path) {
    //   this.navigateTo({ path, component: ThfPageDynamicEditComponent });
    // } else {
    //   window.history.back();
    // }
  }

  private loadData(id) {
    this.thfPageDynamicService.getResource(id).toPromise().then(response => {
      this.model = response;
    }).catch(() => {
      this.model = undefined;
      this.actions = undefined;
    });
  }

  private loadMetadata(id) {
    this.thfPageDynamicService.getMetadata('detail').toPromise().then(response => {
      this.autoRouter = response.autoRouter;
      this.actions = response.actions || {};
      this.breadcrumb = response.breadcrumb || { items : [] };
      this.fields = response.fields || [];
      this.title = response.title;

      this.loadData(id);
    });
  }

  // @todo Validar rotas na mÃ£o pois se existir uma rota '**' o catch do navigation nÃ£o funciona.
  private navigateTo(route: { path: string, component?, url?: string, params?: any }, forceStopAutoRouter: boolean = false) {
    this.router.navigate([route.url || route.path], { queryParams: route.params })
      .catch(() => {
        if (forceStopAutoRouter || !this.autoRouter) {
          return;
        }

        this.router.config.unshift(<Route>{
          path: route.path, component: route.component, data: { serviceApi: this.serviceApi, autoRouter: true }
        });

        this.navigateTo(route, true);
      });
  }

  private openEdit(path) {
    const url = this.resolveUrl(this.model, path);

    // this.navigateTo({ path, url, component: ThfPageDynamicEditComponent });
    this.navigateTo({ path, url });
  }

  private remove(path) {
    const uniqueKey = this.formatUniqueKey(this.model);

    this.thfPageDynamicService.deleteResource(uniqueKey).toPromise().then(() => {
      this.thfNotification.success(this.literals.removeNotificationSuccess);

      this.navigateTo({ path: path });
      // this.navigateTo({ path: path, component: ThfPageDynamicTableComponent });
    });
  }

  private resolveUrl(item: any, path: string) {
    const uniqueKey = this.formatUniqueKey(item);

    return path.replace(/:id/g, uniqueKey);
  }

  private getPageActions(actions: ThfPageDynamicDetailActions = {}): Array<ThfPageAction> {
    const pageActions = [];

    if (actions.edit) {
      pageActions.push({ label: this.literals.pageActionEdit, action: this.openEdit.bind(this, actions.edit) });
    }

    if (actions.remove) {
      pageActions.push({ label: this.literals.pageActionRemove, action: this.confirmRemove.bind(this, actions.remove) });
    }

    if (actions.back === undefined || actions.back) {
      pageActions.push({ label: this.literals.pageActionBack, action: this.goBack.bind(this, actions.back) });
    }

    return pageActions;
  }

  private getKeysByFields(fields: Array<any> = []) {
    return fields.filter(field => field.key === true).map(field => field.property);
  }

  private getDuplicatesByFields(fields: Array<any> = []) {
    return fields.filter(field => field.duplicate === true).map(field => field.property);
  }

  private isObject(value: any): boolean {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

}
